import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query'
import {api} from '@/lib/api'
import {track} from '@/lib/telemetry'
import type {UploadTarget} from '@/hooks/sgsst/evidencias/use-evidence'
import type {components} from '@/lib/api-schema'

export type Solicitud = components['schemas']['SolicitudRead']
export type MemberSolicitud = components['schemas']['MemberSolicitudRead']
export type MemberSolicitudList = components['schemas']['MemberSolicitudListRead']
export type SolicitudState = Solicitud['state']
type SolicitudPage = components['schemas']['Page_SolicitudRead_']

// El panel necesita todas las del año abierto; 100 es el tope de página.
const PAGE_SIZE = 100

// `PERSONAS_MAX` de `backend/app/sgsst/evidencias/core/solicitudes.py`: solo topa a las elegidas una por una.
export const PERSONAS_POR_SOLICITUD = 200

// `SolicitudCreate.note` de `backend/app/sgsst/evidencias/schemas.py`.
export const TOPE_NOTA_SOLICITUD = 300

export function useSolicitudes(businessId: string | undefined, year?: number) {
  return useQuery({
    queryKey: ['solicitudes', businessId, year ?? 'current'],
    queryFn: async () =>
      (
        await api.get<SolicitudPage>(`/businesses/${businessId}/sgsst/solicitudes`, {
          params: {page_size: PAGE_SIZE, ...(year ? {year} : {})},
        })
      ).data.items,
    enabled: !!businessId,
  })
}

/** Lo que la persona aportó a una solicitud suya; abrir uno de salud queda registrado. */
export function rutaDeMiAporte(businessId: string, evidenceId: string): string {
  return `/businesses/${businessId}/mi/aportes/${evidenceId}/descarga`
}

export function useMySolicitudes(businessId: string | undefined) {
  return useQuery({
    queryKey: ['mi-solicitudes', businessId],
    queryFn: async () => (await api.get<MemberSolicitudList>(`/businesses/${businessId}/mi/solicitudes`)).data,
    enabled: !!businessId,
  })
}

export function useCreateSolicitudes(businessId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: {
      standard_code: string
      employee_ids: string[]
      // A quiénes alcanza lo resuelve el servidor: sin tope y sin repetirle la solicitud a quien ya la tiene abierta.
      todos_los_activos: boolean
      note: string
      due_date: string
      // Qué documento esperado se pide; el archivo aportado lo hereda en el servidor.
      soporte_key?: string | null
    }) => api.post(`/businesses/${businessId}/sgsst/solicitudes`, input),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({queryKey: ['solicitudes', businessId]})
      track('solicitud_created', {business_id: businessId, standard_code: variables.standard_code})
    },
  })
}

export function useCancelSolicitud(businessId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (solicitudId: string) => api.delete(`/businesses/${businessId}/sgsst/solicitudes/${solicitudId}`),
    onSuccess: () => queryClient.invalidateQueries({queryKey: ['solicitudes', businessId]}),
  })
}

export async function mintMemberUploadTarget(
  businessId: string,
  solicitudId: string,
  file: File
): Promise<UploadTarget> {
  return (
    await api.post<UploadTarget>(`/businesses/${businessId}/mi/solicitudes/${solicitudId}/upload-target`, {
      filename: file.name,
      content_type: file.type,
      size_bytes: file.size,
    })
  ).data
}

export function useAportarSolicitud(businessId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: {solicitudId: string; objectKey: string; file: File}) =>
      api.post(`/businesses/${businessId}/mi/solicitudes/${input.solicitudId}/aportar`, {
        object_key: input.objectKey,
        filename: input.file.name,
        content_type: input.file.type,
        size_bytes: input.file.size,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['mi-solicitudes', businessId]})
      // The fulfillment becomes evidence and moves the standard's coverage/readiness.
      queryClient.invalidateQueries({queryKey: ['evidence', businessId]})
      queryClient.invalidateQueries({queryKey: ['sgsst', businessId]})
      // Dos lentes del embudo para un solo acto; a telemetría solo sale el id de la empresa.
      track('member_soporte_uploaded', {business_id: businessId})
      track('solicitud_fulfilled', {business_id: businessId})
    },
  })
}
